'use strict'

import React, { useState, useEffect } from 'react'
import { SpinnerButton } from '../../../common/CustomButton.js'
import { getTableKey, getTableName } from '../extra/helpers'

const FORBIDDEN_CHARS = /[,\\"`]/
const MAX_MEMO = 200

/**
 * 変換表設定モーダルの本文＋フッター（共通 useModal の body 用）
 *
 * @param {Object} props
 * @param {'new' | 'edit'} props.mode - モード
 * @param {import('../extra/types').ConversionTableMeta | null} [props.initialMeta] - 編集時の既存メタ
 * @param {import('../extra/types').ConversionTableRow[]} [props.tableList] - 変換表一覧（重複チェック用）
 * @param {function(): void} props.onClose - 閉じるハンドラー
 * @param {function(Object): void} props.onSave - 保存ハンドラー（{ name, memo }）
 * @returns {JSX.Element}
 */
const ConversionTableModalContent = ({ mode, initialMeta, tableList, onClose, onSave }) => {
  const [name, setName] = useState('')
  const [memo, setMemo] = useState('')
  const [nameError, setNameError] = useState('')
  const [memoError, setMemoError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (mode === 'edit' && initialMeta) {
      setName(initialMeta.name ?? '')
      setMemo(initialMeta.memo ?? '')
    } else {
      setName('')
      setMemo('')
    }
    setNameError('')
    setMemoError('')
    setIsSubmitting(false)
  }, [mode, initialMeta])

  useEffect(() => {
    const modalWindowElement = document.querySelector('.modal-window')
    const modalWindowClassList = ['w-[initial]', 'max-h-[97vh]', 'max-w-[750px]', 'overflow-y-auto']

    modalWindowElement?.classList.add(...modalWindowClassList)
    return () => {
      modalWindowElement?.classList.remove(...modalWindowClassList)
    }
  }, [])

  /**
   * 変換表名のバリデーション
   *
   * @param {string} name - 変換表名
   * @param {Array} tableList - 変換表一覧
   * @param {string} mode - モード（'new' | 'edit'）
   * @param {Object} initialMeta - 編集時の初期メタデータ
   * @returns {string | null} エラーメッセージ（エラーがない場合はnull）
   */
  const validateName = (name, tableList, mode, initialMeta) => {
    if (!name.trim()) {
      return '変換表名は必須です。'
    }

    if (FORBIDDEN_CHARS.test(name)) {
      return '次の記号（, \\ "）は入力できません。'
    }

    const trimmedName = name.trim()
    const isDuplicate = (tableList || []).some((table) => {
      const tableName = getTableName(table)
      const tableKey = getTableKey(table)

      if (mode === 'new') {
        return tableName === trimmedName
      }

      if (mode === 'edit' && initialMeta) {
        const currentKey = initialMeta.key || ''
        return tableName === trimmedName && tableKey !== currentKey
      }

      return false
    })

    if (isDuplicate) {
      return '同一の変換表は既に存在します。'
    }

    return null
  }

  /**
   * 説明のバリデーション
   *
   * @param {string} memo - 説明
   * @returns {string | null} エラーメッセージ（エラーがない場合はnull）
   */
  const validateMemo = (memo) => {
    if (memo.length > MAX_MEMO) {
      return `説明は${MAX_MEMO}文字まで入力できます。`
    }
    return null
  }

  /**
   * フォーム全体のバリデーション
   *
   * @returns {boolean} バリデーション結果（trueの場合は成功）
   */
  const validate = () => {
    const nameErr = validateName(name, tableList, mode, initialMeta)
    const memoErr = validateMemo(memo)

    setNameError(nameErr || '')
    setMemoError(memoErr || '')

    return !nameErr && !memoErr
  }

  const handleSave = async () => {
    if (!validate()) return

    setIsSubmitting(true)
    try {
      await onSave({ name: name.trim(), memo: memo.trim() })
      // onSave内でモーダルが閉じられるため、成功時はここに到達しない
    } catch (err) {
      // エラー時のみisSubmittingをfalseに戻す（モーダルは開いたまま）
      setIsSubmitting(false)
      console.error('保存処理でエラーが発生しました:', err)
    }
  }

  return (
    <>
      <table className="form-table table-fixed w-full text-base">
        <tbody>
          <tr>
            <th className="form-table__col-head w-[150px]">
              <label htmlFor="conversionTableName" className="text-h7 font-bold">
                変換表名 <span className="red--text">*</span>
              </label>
            </th>
            <td className="form-control form-table__col-data">
              <input
                id="conversionTableName"
                name="conversionTableName"
                className={`text-input w-full text-left ${nameError ? 'is-error' : ''}`}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="変換表名を入力してください"
                data-testid="conversion-table-modal-name"
              />
              {nameError && <p className="red--text">{nameError}</p>}
            </td>
          </tr>
          <tr>
            <th className="form-table__col-head w-[150px]">
              <label htmlFor="conversionTableMemo" className="text-h7 font-bold">
                説明
              </label>
            </th>
            <td className="form-control form-table__col-data">
              <textarea
                id="conversionTableMemo"
                name="conversionTableMemo"
                className={`text-input w-full text-left ${memoError ? 'is-error' : ''}`}
                placeholder="説明文を入力してください(全角200文字程度)"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                rows={3}
                style={{ minHeight: 80 }}
              />
              {memoError && <p className="red--text">{memoError}</p>}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="modal-content-footer mt-3">
        <div className="footer_button_area gap-x-16">
          <button type="button" className="footer-btn button_back" onClick={onClose} disabled={isSubmitting}>
            キャンセル
          </button>
          <SpinnerButton
            name="登録"
            className="footer-btn button_base"
            onClick={handleSave}
            disabled={isSubmitting}
            data-testid="conversion-table-modal-register"
          />
        </div>
      </div>
    </>
  )
}

export default ConversionTableModalContent
